import { Link } from "@inertiajs/react";
import { Badge } from "@/components/ui/badge";
import type HomeVerificationTask from "@/types/serializers/HomeVerificationTask";

type RelationshipType = HomeVerificationTask["relationship"];

const DIRECT_CLIENT_LABELS: Record<
	"direct_individual" | "direct_entrepreneur",
	string
> = {
	direct_individual: "Fyzická osoba",
	direct_entrepreneur: "OSVČ",
};

function isDirectRelationship(
	relationship: RelationshipType,
): relationship is "direct_individual" | "direct_entrepreneur" {
	return (
		relationship === "direct_individual" ||
		relationship === "direct_entrepreneur"
	);
}

function getClientDisplayText(task: HomeVerificationTask): string {
	if (isDirectRelationship(task.relationship)) {
		return DIRECT_CLIENT_LABELS[task.relationship];
	}
	return task.client_name;
}

function formatDate(dateString: string): string {
	return new Date(dateString).toLocaleDateString("cs-CZ");
}

function formatExpiredAgo(daysUntilExpiry: number): string {
	const daysAgo = Math.abs(daysUntilExpiry);
	if (daysAgo === 0) return "dnes";
	if (daysAgo === 1) return "před 1 dnem";
	return `před ${daysAgo} dny`;
}

interface VerificationTaskItemProps {
	task: HomeVerificationTask;
	isExpired?: boolean;
}

export function VerificationTaskItem({
	task,
	isExpired = false,
}: VerificationTaskItemProps) {
	const descriptionText = isExpired
		? `identifikace prošla ${formatExpiredAgo(task.days_until_expiry)}`
		: `identifikace expiruje ${formatDate(task.expiry_date)}`;

	const baseClassName =
		"flex items-center justify-between rounded-lg border p-3 transition-colors hover:bg-muted";
	const expiredClassName = isExpired ? "bg-destructive/10" : "";

	const badgeClassName = isExpired ? "bg-card text-xs" : "text-xs";

	return (
		<Link
			href={`/clients/${task.client_id}`}
			className={`${baseClassName} ${expiredClassName}`}
		>
			<div className="flex items-center gap-2">
				<span className="font-medium">{task.person_name}</span>
				<Badge variant="outline" className={badgeClassName}>
					{getClientDisplayText(task)}
				</Badge>
			</div>
			<span className="text-muted-foreground text-sm">{descriptionText}</span>
		</Link>
	);
}
